import React, { useRef, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { heroContent } from "../mock";
import { sendContactEmail, createMailtoLink } from "../services/emailService";
import { useModeContext } from "../contexts/ModeContext";
import { SuccessModal } from "./ui/success-modal";
import { ErrorModal } from "./ui/error-modal";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "./ui/form";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { HighlightText } from "./ui/animated-reveal-text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

gsap.registerPlugin(useGSAP);

const createSchema = (activeMode) => z.object({
  name: z.string().min(2, activeMode === 0 ? "Bitte gib deinen Namen ein." : "Bitte geben Sie Ihren Namen ein."),
  email: z.string().email("Bitte eine gültige E‑Mail angeben."),
  phone: z.string().optional(),
  company: activeMode === 1 ? z.string().optional() : z.string().optional(),
  topic: z.string().min(2, activeMode === 0 ? "Bitte ein Thema angeben." : "Bitte geben Sie ein Thema an."),
  competence: z.enum(
    activeMode === 0 
      ? ["Einzelcoaching", "Gruppencoaching", "Keynote-Seminare"] 
      : ["Executive Coaching", "Leadership Development", "Keynote-Speaking"], 
    { required_error: activeMode === 0 ? "Bitte Kompetenz wählen." : "Bitte wählen Sie eine Kompetenz." }
  ),
  datetime: z.string()
    .min(1, activeMode === 0 ? "Bitte Datum und Uhrzeit wählen." : "Bitte wählen Sie Datum und Uhrzeit.")
    .refine((value) => {
      if (!value) return false;
      const selectedDate = new Date(value);
      const dayOfWeek = selectedDate.getDay();
      const hours = selectedDate.getHours();
      
      // Check if weekend
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return false;
      }
      
      // Check business hours (9-18)
      if (hours < 9 || hours >= 18) {
        return false;
      }
      
      return true;
    }, {
      message: "Termine sind nur Montag-Freitag von 9:00-18:00 Uhr verfügbar. Wochenendtermine auf Anfrage."
    }),
  privacy: z.literal(true, { errorMap: () => ({ message: "Bitte Datenschutz bestätigen." }) }),
});

const steps = [
  { id: "personal", title: "Persönlich" },
  { id: "details", title: "Details" },
  { id: "timing", title: "Termine" },
];

// Function to get next business day with random business hour
const getNextBusinessDayRandomTime = () => {
  // Start with current date in local timezone
  const now = new Date();
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Start with tomorrow
  date.setDate(date.getDate() + 1);
  
  // Skip weekends - ensure it's a weekday (Monday=1 to Friday=5)
  // Sunday = 0, Monday = 1, ..., Saturday = 6
  let attempts = 0;
  while ((date.getDay() === 0 || date.getDay() === 6) && attempts < 10) {
    date.setDate(date.getDate() + 1);
    attempts++;
  }
  
  // Double check: if still weekend, force to Monday
  if (date.getDay() === 0 || date.getDay() === 6) {
    // Find next Monday
    const daysUntilMonday = (8 - date.getDay()) % 7;
    date.setDate(date.getDate() + daysUntilMonday);
  }
  
  // Random business hour between 9-18 (only even hours)
  const businessHours = [9, 10, 12, 14, 16, 18];
  const randomHour = businessHours[Math.floor(Math.random() * businessHours.length)];
  
  // Set to random hour with 00 minutes, 00 seconds, 00 milliseconds
  date.setHours(randomHour, 0, 0, 0);
  
  // Format for datetime-local input (YYYY-MM-DDTHH:MM)
  // Use local timezone formatting to avoid timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  const formatted = `${year}-${month}-${day}T${hours}:${minutes}`;
  
  // Debug log to check what we're generating
  console.log('Generated datetime:', {
    dayOfWeek: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'][date.getDay()],
    date: date.toLocaleDateString('de-DE'),
    time: `${hours}:${minutes}`,
    formatted: formatted
  });
  
  return formatted;
};

const BookingSection = () => {
  const { activeMode } = useModeContext();
  const root = useRef(null);
  const galleryRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentFormData, setCurrentFormData] = useState(null);

  useGSAP(
    () => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from(".booking-title", { y: 30, opacity: 0, duration: 0.6 })
        .from(".booking-steps", { y: 20, opacity: 0, duration: 0.5 }, "-0.2")
        .from(".booking-card", { y: 20, opacity: 0, duration: 0.6 }, "-0.1");

      if (galleryRef.current) {
        const leftCards = galleryRef.current.querySelectorAll(".gallery-left");
        const rightCards = galleryRef.current.querySelectorAll(".gallery-right");
        
        // Initial setup - alle an ihrer Startposition
        gsap.set(leftCards, { y: 0 });
        gsap.set(rightCards, { y: 0 });
        
        // Entry animation
        gsap.fromTo(leftCards, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.8, ease: "power2.out", stagger: 0.1, delay: 0.3 });
        gsap.fromTo(rightCards, { opacity: 0, y: -30 }, { opacity: 1, y: 0, duration: 0.8, ease: "power2.out", stagger: 0.1, delay: 0.3 });
        
        // Continuous loop - left cards move up, right cards move down
        gsap.to(leftCards, { 
          y: -20, 
          duration: 3, 
          ease: "sine.inOut", 
          repeat: -1, 
          yoyo: true,
          delay: 1.5,
          stagger: { each: 0.2, repeat: -1, yoyo: true }
        });
        
        gsap.to(rightCards, { 
          y: 20, 
          duration: 3, 
          ease: "sine.inOut", 
          repeat: -1, 
          yoyo: true,
          delay: 1.5,
          stagger: { each: 0.2, repeat: -1, yoyo: true }
        });
      }
    },
    { scope: root }
  );

  const form = useForm({
    resolver: zodResolver(createSchema(activeMode)),
    defaultValues: { 
      name: "", 
      email: "", 
      phone: "", 
      company: "", 
      topic: "", 
      competence: undefined, 
      datetime: getNextBusinessDayRandomTime(), // Vorausgefüllt mit nächstem Werktag, zufällige gerade Stunde
      privacy: false 
    },
    mode: "onBlur",
  });

  // Reset form when mode changes
  useEffect(() => {
    setCurrentStep(0);
    form.reset({
      name: "", 
      email: "", 
      phone: "", 
      company: "", 
      topic: "", 
      competence: undefined, 
      datetime: getNextBusinessDayRandomTime(), // Auch beim Reset vorausfüllen
      privacy: false 
    });
    form.clearErrors();
  }, [activeMode, form]);

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
      setTimeout(() => {
        const stepEl = document.querySelector(".form-step");
        if (stepEl) {
          gsap.fromTo(stepEl, { opacity: 0, x: 30 }, { opacity: 1, x: 0, duration: 0.4, ease: "power2.out" });
        }
      }, 10);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      setTimeout(() => {
        const stepEl = document.querySelector(".form-step");
        if (stepEl) {
          gsap.fromTo(stepEl, { opacity: 0, x: -30 }, { opacity: 1, x: 0, duration: 0.4, ease: "power2.out" });
        }
      }, 10);
    }
  };

  const isStepValid = () => {
    const formValues = form.getValues();
    switch (currentStep) {
      case 0: return formValues.name?.trim() && formValues.email?.trim();
      case 1: return formValues.competence && formValues.topic?.trim();
      case 2: return formValues.datetime && formValues.privacy;
      default: return true;
    }
  };

  const onSubmit = async (values) => {
    try {
      // Determine mode type based on activeMode
      const modeType = activeMode === 0 ? "Behaviour Change Anfrage" : "Peak Performance Anfrage";
      
      // Prepare form data for EmailJS
      const formData = {
        name: values.name,
        email: values.email,
        phone: values.phone || "",
        company: values.company || "",
        topic: values.topic,
        competence: values.competence,
        datetime: values.datetime ? new Date(values.datetime).toLocaleString('de-DE') : "",
        mode_type: modeType,
        privacy: values.privacy
      };

      console.log("Sending contact form data:", formData);
      setCurrentFormData(formData);

      // Send email using EmailJS
      const result = await sendContactEmail(formData);

      if (result.success) {
        // Show success modal
        setShowSuccessModal(true);
        
        // Reset form
        form.reset({
          name: "", 
          email: "", 
          phone: "", 
          company: "", 
          topic: "", 
          competence: undefined, 
          datetime: getNextBusinessDayRandomTime(), // Auch nach erfolgreichem Senden vorausfüllen
          privacy: false 
        });
        setCurrentStep(0);
      } else {
        // Show error modal
        setErrorMessage(result.message);
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      
      // Show error modal for any unexpected errors
      setErrorMessage("Es gab ein unerwartetes Problem beim Versenden Ihrer Nachricht.");
      setShowErrorModal(true);
    }
  };

  const handleRetry = () => {
    setShowErrorModal(false);
    if (currentFormData) {
      // Retry sending with current form data
      sendContactEmail(currentFormData).then(result => {
        if (result.success) {
          setShowSuccessModal(true);
        } else {
          setErrorMessage(result.message);
          setShowErrorModal(true);
        }
      });
    }
  };

  const handleMailto = () => {
    setShowErrorModal(false);
    if (currentFormData) {
      const mailtoLink = createMailtoLink(currentFormData);
      window.location.href = mailtoLink;
    }
  };

  const c = heroContent.colors;

  return (
    <section id="termin" ref={root} className={`relative w-full h-auto md:h-auto md:min-h-[100svh] pt-[96px] md:pt-[80px] pb-8 md:pb-16 bg-white dark:bg-[#0B0B0C] dark:text-neutral-200 overflow-visible ${activeMode === 1 ? 'z-[100]' : 'z-50'}`}>
      <div className="mx-auto max-w-7xl px-6 h-auto md:h-full flex flex-col">
        <h2 className="booking-title text-[clamp(1.25rem,7vw,2.2rem)] md:text-[clamp(1.8rem,6.5vw,5rem)] font-semibold tracking-[-0.04em] whitespace-normal md:whitespace-nowrap text-[#A9BBB5] dark:text-[rgb(229_229_229_/var(--tw-text-opacity,1))]">
          {activeMode === 0 
            ? "Bereit für echte Veränderung?"
            : "Bereit für Peak Performance?"
          }
        </h2>
        <div className="booking-steps mt-2 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <HighlightText
              as="p"
              text={activeMode === 0 
                ? "1. Kontaktaufnahme / 2. Persönliches Gespräch / 3. Veränderung"
                : "1. Kontaktaufnahme / 2. Strategisches Gespräch / 3. Umsetzung"
              }
              textClassName={`text-[clamp(0.6rem,2.2vw,1rem)] font-[600] whitespace-nowrap ${activeMode === 0 ? 'text-white' : 'text-[#161616]'}`}
              highlightClassName={`rounded-md ${activeMode === 1 ? 'shine-loop' : ''}`}
              highlightColor="var(--brand-accent)"
              shineOnHover
              shineColor="rgba(255,255,255,0.55)"
              duration={0.9}
              ease="easeInOut"
              organic={false}
            />
          </div>
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 border bg-amber-50 text-amber-700 border-amber-200 dark:bg-[#151515] dark:text-[var(--brand-accent)] dark:border-[#302826]">
            <span className="w-2 h-2 rounded-full bg-amber-500 dark:bg-[var(--brand-accent)] animate-pulse" />
            <span className="text-[clamp(0.55rem,1.8vw,0.875rem)] whitespace-nowrap">
              {activeMode === 0 
                ? "Begrenzte Plätze pro Monat – sichere dir jetzt ein Kennenlerngespräch."
                : "Exklusiv für Unternehmer:innen, Führungskräfte & High Performer."
              }
            </span>
          </div>
        </div>

        <div className={`booking-card mt-4 flex-1 p-0 md:p-0 grid grid-cols-1 md:grid-cols-2 gap-8 items-start relative ${activeMode === 1 ? 'z-[110]' : 'z-[60]'}`} style={{ minHeight: 'auto' }}>
          <div style={{ paddingBottom: '2rem' }}>
            {/* Friendly intro text */}
            <div className="mb-6 p-4 rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
              <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">
                {activeMode === 0
                  ? "Als Behaviour Change Coach freue ich mich darauf, dich kennenzulernen. Erzähl mir in wenigen Schritten von deinen Zielen – gemeinsam entwickeln wir nachhaltige Verhaltensänderungen, die wirklich funktionieren."
                  : "Schildern Sie mir in wenigen Schritten Ihre Ziele – gemeinsam entwickeln wir strategische Lösungen für nachhaltige Höchstleistung."
                }
              </p>
            </div>
            
            {/* Progress indicator */}
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                {steps.map((step, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index < currentStep
                          ? "bg-[var(--brand-accent)]"
                          : index === currentStep
                          ? "ring-4 ring-[color-mix(in_srgb,var(--brand-accent)_60%,transparent)] shadow-lg backdrop-blur-sm"
                          : "bg-neutral-300 dark:bg-neutral-700"
                      }`}
                      style={index === currentStep ? {
                        background: "linear-gradient(135deg, color-mix(in_srgb, var(--brand-accent) 95%, #000 5%) 0%, var(--brand-accent) 100%)",
                        backdropFilter: "blur(4px)",
                        border: "1px solid rgba(255, 255, 255, 0.2)"
                      } : {}}
                    />
                    <span
                      className={`text-xs mt-1.5 ${
                        index === currentStep
                          ? "text-amber-600 dark:text-[var(--brand-accent)] font-medium"
                          : "text-neutral-500 dark:text-neutral-400"
                      }`}
                    >
                      {step.title}
                    </span>
                  </div>
                ))}
              </div>
              <div className="w-full bg-neutral-200 dark:bg-neutral-800 h-1 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 dark:bg-[var(--brand-accent)] transition-all duration-300"
                  style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                />
              </div>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className={`space-y-6 relative ${activeMode === 1 ? 'z-[110]' : 'z-[60]'}`}>
                {/* Hidden field for mode type */}
                <input 
                  type="hidden" 
                  name="mode_type" 
                  value={activeMode === 0 ? "Behaviour Change Anfrage" : "Peak Performance Anfrage"} 
                />
                <div className={`form-step space-y-6 relative ${activeMode === 1 ? 'z-[110]' : 'z-[60]'}`}>
                  {/* Step 1: Personal Info */}
                  {currentStep === 0 && (
                    <div className="space-y-5">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem className="space-y-2">
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                              <Input placeholder={activeMode === 0 ? "Dein Name" : "Ihr Name"} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {activeMode === 1 && (
                        <FormField
                          control={form.control}
                          name="company"
                          render={({ field }) => (
                            <FormItem className="space-y-2">
                              <FormLabel>Unternehmen (optional)</FormLabel>
                              <FormControl>
                                <Input placeholder="Ihr Unternehmen" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem className="space-y-2">
                              <FormLabel>E‑Mail</FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="name@mail.com" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem className="space-y-2">
                              <FormLabel>Telefon (optional)</FormLabel>
                              <FormControl>
                                <Input type="tel" placeholder={activeMode === 0 ? "+49 …" : "+49 …"} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 2: Details */}
                  {currentStep === 1 && (
                    <div className="space-y-5">
                      <FormField
                        control={form.control}
                        name="competence"
                        render={({ field }) => (
                          <FormItem className="space-y-2">
                            <FormLabel>Welche Kompetenz?</FormLabel>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Bitte wählen" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className={activeMode === 1 ? 'z-[9999]' : ''}>
                                {activeMode === 0 ? (
                                  <>
                                    <SelectItem value="Einzelcoaching">Einzelcoaching</SelectItem>
                                    <SelectItem value="Gruppencoaching">Gruppencoaching</SelectItem>
                                    <SelectItem value="Keynote-Seminare">Keynote‑Seminare</SelectItem>
                                  </>
                                ) : (
                                  <>
                                    <SelectItem value="Executive Coaching">Executive Coaching</SelectItem>
                                    <SelectItem value="Leadership Development">Leadership Development</SelectItem>
                                    <SelectItem value="Keynote-Speaking">Keynote‑Speaking</SelectItem>
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="topic"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Worum geht es?</FormLabel>
                            <FormControl>
                              <Textarea rows={4} placeholder={activeMode === 0 ? "Kurz dein Anliegen oder Ziel beschreiben" : "Kurz Ihr Anliegen oder Ziel beschreiben"} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Step 3: Timing */}
                  {currentStep === 2 && (
                    <div className="space-y-5">
                      <FormField
                        control={form.control}
                        name="datetime"
                        render={({ field }) => {
                          // Calculate minimum datetime (tomorrow at 9 AM)
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          tomorrow.setHours(9, 0, 0, 0);
                          const minDateTime = tomorrow.toISOString().slice(0, 16);

                          const validateDateTime = (value) => {
                            if (!value) return true; // Let required validation handle empty values
                            
                            const selectedDate = new Date(value);
                            const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 6 = Saturday
                            const hours = selectedDate.getHours();
                            
                            // Check if weekend
                            if (dayOfWeek === 0 || dayOfWeek === 6) {
                              return "Wochenendtermine sind nur auf Anfrage möglich. Bitte wählen Sie einen Werktag.";
                            }
                            
                            // Check business hours (9-18)
                            if (hours < 9 || hours >= 18) {
                              return "Bitte wählen Sie eine Uhrzeit zwischen 9:00 und 18:00 Uhr.";
                            }
                            
                            return true;
                          };

                          const handleChange = (e) => {
                            const value = e.target.value;
                            field.onChange(value);
                            
                            // Validate immediately
                            const validation = validateDateTime(value);
                            if (validation !== true) {
                              form.setError("datetime", { 
                                type: "manual", 
                                message: validation 
                              });
                            } else {
                              form.clearErrors("datetime");
                            }
                          };

                          return (
                            <FormItem className="space-y-2">
                              <FormLabel>Wunschtermin</FormLabel>
                              <FormControl>
                                <input
                                  type="datetime-local"
                                  min={minDateTime}
                                  value={field.value || ""}
                                  onChange={handleChange}
                                  onBlur={field.onBlur}
                                  name={field.name}
                                  ref={field.ref}
                                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                  placeholder="Datum und Uhrzeit wählen"
                                />
                              </FormControl>
                              <FormMessage />
                              <div className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
                                Verfügbar: Montag-Freitag, 9:00-18:00 Uhr
                              </div>
                            </FormItem>
                          );
                        }}
                      />

                      {/* Wochenend-Hinweis */}
                      <div className="mt-3">
                        <p className="text-xs text-neutral-600 dark:text-neutral-400">
                          <a 
                            href="mailto:mail@tom-oppenhaeuser.com?subject=Exklusiver%20Wochenendtermin%20Anfrage&body=Hallo%20Tom%2C%0A%0Aich%20hätte%20gerne%20einen%20exklusiven%20Termin%20am%20Wochenende.%0A%0AMeine%20Wunschzeit%3A%20%0AMein%20Anliegen%3A%20%0A%0AVielen%20Dank!"
                            className="text-amber-600 dark:text-[var(--brand-accent)] hover:text-amber-800 underline transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 rounded"
                          >
                            Exklusive Termine am Wochenende sind auf Anfrage möglich.
                          </a>
                        </p>
                      </div>

                      <label className="flex items-start gap-3 text-sm text-neutral-700 dark:text-neutral-300 mt-6">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-neutral-300 dark:border-neutral-700 text-amber-500 focus:ring-amber-500 focus:ring-offset-0"
                          {...form.register("privacy")}
                        />
                        <span>
                          Ich habe die Datenschutzhinweise gelesen und stimme der Verarbeitung meiner Angaben zur
                          Kontaktaufnahme zu. Der Widerruf ist jederzeit möglich.
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Navigation */}
                  <div className={`flex justify-between pt-8 pb-4 relative ${activeMode === 1 ? 'z-[120]' : 'z-[70]'}`}>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={prevStep}
                      disabled={currentStep === 0}
                      className={`flex items-center gap-1 relative ${activeMode === 1 ? 'z-[120]' : 'z-[70]'}`}
                    >
                      ← Zurück
                    </Button>
                    <Button
                      type={currentStep === steps.length - 1 ? "submit" : "button"}
                      onClick={currentStep === steps.length - 1 ? undefined : nextStep}
                      disabled={!isStepValid()}
                      className={`rounded-full h-11 px-6 text-[15px] relative ${activeMode === 1 ? 'z-[120]' : 'z-[70]'} ${
                        currentStep === steps.length - 1 && activeMode === 1
                          ? "bg-[var(--brand-accent)] hover:bg-[#C4956D] text-[#161616] dark:bg-[var(--brand-accent)] dark:hover:bg-[#C4956D] dark:text-[#161616]"
                          : "bg-neutral-900 hover:bg-neutral-800 text-white"
                      }`}
                    >
                      {currentStep === steps.length - 1 ? "Termin anfragen" : "Weiter →"}
                    </Button>
                  </div>
                </div>
              </form>
            </Form>
          </div>
          <div className="hidden md:block">
            <div ref={root} className="rounded-xl ring-1 ring-black/5 dark:ring-white/10 shadow-sm p-4 bg-white dark:bg-neutral-900 scale-90">
              <div ref={galleryRef} className="grid grid-cols-2 grid-rows-[50px_150px_50px_150px_50px] gap-4">
                {/* Right column (swipe down) */}
                <div className="gallery-cell gallery-right col-start-2 col-end-3 row-start-1 row-end-3 relative rounded-xl shadow-xl group overflow-hidden">
                  <div className="mask mask-right h-full w-full origin-top will-change-transform">
                    <img className="size-full object-cover transition-transform duration-500 group-hover:scale-105 filter grayscale group-hover:grayscale-0 transition-all" src="/bild1.jpg" alt="Professionelles Einzelcoaching: Intensives Gespräch zwischen Coach und Klient in ruhiger Atmosphäre" />
                  </div>
                </div>
                {/* Left column (swipe up) */}
                <div className="gallery-cell gallery-left col-start-1 col-end-2 row-start-2 row-end-4 relative rounded-xl shadow-xl group overflow-hidden">
                  <div className="mask mask-left h-full w-full origin-bottom will-change-transform">
                    <img className="size-full object-cover transition-transform duration-500 group-hover:scale-105 filter grayscale group-hover:grayscale-0 transition-all" src="/bild2.jpg" alt="Teamcoaching und Gruppenarbeit: Kollaborative Sitzung mit mehreren Teilnehmern" />
                  </div>
                </div>
                <div className="gallery-cell gallery-left col-start-1 col-end-2 row-start-4 row-end-6 relative rounded-xl shadow-xl group overflow-hidden">
                  <div className="mask mask-left h-full w-full origin-bottom will-change-transform">
                    <img className="size-full object-cover transition-transform duration-500 group-hover:scale-105 filter grayscale group-hover:grayscale-0 transition-all" src="/bild3.jpg" alt="Innovation und Kreativität: Brainstorming und Ideenentwicklung in modernem Arbeitsumfeld" />
                  </div>
                </div>
                <div className="gallery-cell gallery-right col-start-2 col-end-3 row-start-3 row-end-5 relative rounded-xl shadow-xl group overflow-hidden">
                  <div className="mask mask-right h-full w-full origin-top will-change-transform">
                    <img className="size-full object-cover transition-transform duration-500 group-hover:scale-105 filter grayscale group-hover:grayscale-0 transition-all" src="/bild4.jpg" alt="Keynote-Seminar und Workshop: Interaktive Präsentation vor Publikum mit praktischen Übungen" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Nachricht erfolgreich gesendet"
        message="Vielen Dank für Ihre Anfrage! Ich melde mich zeitnah bei Ihnen zurück."
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Nachricht konnte nicht gesendet werden"
        message={errorMessage}
        onRetry={handleRetry}
        onMailto={handleMailto}
      />
    </section>
  );
};

export default BookingSection;


